import { ChangeDetectionStrategy, Component, DEFAULT_CURRENCY_CODE, inject, LOCALE_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, merge, ReplaySubject, Subject } from 'rxjs';
import { filter, map, shareReplay, startWith, take, withLatestFrom } from 'rxjs/operators';
import { CommonModule, formatCurrency, getCurrencySymbol } from '@angular/common';
import { AllocationMode } from '../../../../accounts/shared/types/allocation-mode-type';
import { ButtonToggleComponent, ButtonToggleOption } from '../../button-toggle/button-toggle.component';
import { Alert } from '../../../../../models/alerts/dto/alert';
import { AlertBackgroundColorEnum } from '../../../../../models/alerts/enum/alert-background-color.enum';
import { BaseActionModalComponent } from '../../../modals/base-action-modal/base-action-modal.component';
import { CheckboxInputComponent } from '../../../../../ui/form-components/checkbox-input/checkbox-input.component';
import { FormOptionLabelComponent } from '../../../../../ui/form-components/form-option-label/form-option-label.component';
import { PercentageInputComponent } from '../../../../../ui/form-components/percentage-input/percentage-input.component';
import { FormErrorComponent } from '../../../../../ui/form-components/form-error/form-error.component';
import { CurrencyInputComponent } from '../../../../../ui/form-components/currency-input/currency-input.component';
import { SystemBannerAlertComponent } from '../../alert/components/system-banner-alert/system-banner-alert.component';
import { GoverningJurisdictionNamePipe } from '../../../pipes/governing-jurisdiction-name.pipe';
import { FinalLocationLineComponent } from '../../../../../features/governing-jurisdiction-override/components/final-location-line/final-location-line.component';
import { floorToCents } from '../utils/asset-selection-rules';
import { UnlockFundsModalConfig } from './unlock-funds-modal-config';
import {
  applyPooledUnlock,
  applySingleUnlock,
  ceilToHundredths,
  clearUnlock,
  MAX_UNLOCK_PERCENTAGE,
  savedUnlockEntry,
  summarizeUnlockFundsConfig,
  UnlockFundsModalSummary
} from './unlock-funds-modal-utils';

interface UnlockFundsModalContext {
  config: UnlockFundsModalConfig;
  summary: UnlockFundsModalSummary;
}

@Component({
  selector: 'app-account-asset-selection-unlock-funds-modal',
  standalone: true,
  templateUrl: './account-asset-selection-unlock-funds-modal.component.html',
  styleUrls: ['./account-asset-selection-unlock-funds-modal.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    BaseActionModalComponent,
    CheckboxInputComponent,
    FormOptionLabelComponent,
    ButtonToggleComponent,
    PercentageInputComponent,
    FormErrorComponent,
    CurrencyInputComponent,
    SystemBannerAlertComponent,
    GoverningJurisdictionNamePipe,
    FinalLocationLineComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountAssetSelectionUnlockFundsModalComponent {
  private readonly ngbActiveModal = inject(NgbActiveModal);
  private readonly locale = inject(LOCALE_ID);
  private readonly currencyCode = inject(DEFAULT_CURRENCY_CODE);

  public readonly form = new FormGroup({
    percentage: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01), Validators.max(100)]),
    amount: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    useMax: new FormControl<boolean>(false, { nonNullable: true }),
    revertToOriginal: new FormControl<boolean>(false, { nonNullable: true })
  });

  public formSubmitted = false;

  private readonly _context = new ReplaySubject<UnlockFundsModalContext>(1);
  public readonly summary$ = this._context.pipe(map(({ summary }) => summary));

  private readonly _allocationMode = new BehaviorSubject<AllocationMode>('percent');
  public readonly allocationMode$ = this._allocationMode.asObservable();

  // The mode whose control holds the value the user actually entered. Conversions always derive from
  // it so repeated toggles can't compound the rounding each conversion applies. Null means both
  // controls are already in sync (e.g. Use Max set them together) and neither needs converting.
  private readonly _sourceMode = new BehaviorSubject<AllocationMode | null>(null);

  private readonly _amountBlurred = new Subject<void>();

  public showUnlockSection$ = this.form.controls.revertToOriginal.valueChanges.pipe(
    startWith(false),
    map(revert => !revert)
  );

  // The warning waits for the user to leave the input rather than flashing while they type. Use Max
  // writes its values with emitEvent: false, so re-check on useMax too and read the controls directly.
  public warningAlert$ = combineLatest([
    this.summary$,
    this.allocationMode$,
    merge(this._amountBlurred, this.form.controls.useMax.valueChanges).pipe(startWith(null))
  ]).pipe(
    map(([summary, mode]) => {
      if (!summary.isMaxJurisdiction) return null;
      const activeControl = mode === 'percent' ? this.form.controls.percentage : this.form.controls.amount;
      // Don't show the soft-max warning when the hard max validator has already fired
      if (activeControl.hasError('max')) return null;
      const isAboveSoftMax =
        mode === 'percent'
          ? (this.form.controls.percentage.value ?? 0) > MAX_UNLOCK_PERCENTAGE
          : (this.form.controls.amount.value ?? 0) > summary.maxDollarAmount;
      return isAboveSoftMax ? this.buildWarningAlert(mode, summary.maxDollarAmount) : null;
    }),
    shareReplay(1)
  );

  private listenToUseMax = this.form.controls.useMax.valueChanges
    .pipe(takeUntilDestroyed(), filter(Boolean), withLatestFrom(this.summary$))
    .subscribe(([, summary]) => {
      this.form.controls.percentage.setValue(MAX_UNLOCK_PERCENTAGE, { emitEvent: false });
      this.form.controls.amount.setValue(summary.maxDollarAmount, { emitEvent: false });
      this._sourceMode.next(null);
    });

  private listenToClearUseMax = merge(
    this.form.controls.percentage.valueChanges,
    this.form.controls.amount.valueChanges
  )
    .pipe(
      takeUntilDestroyed(),
      filter(() => this.form.controls.useMax.value)
    )
    .subscribe(() => this.form.controls.useMax.setValue(false, { emitEvent: false }));

  private listenToUserEdits = merge(
    this.form.controls.percentage.valueChanges.pipe(map(() => 'percent' as AllocationMode)),
    this.form.controls.amount.valueChanges.pipe(map(() => 'dollar' as AllocationMode))
  )
    .pipe(takeUntilDestroyed())
    .subscribe(mode => this._sourceMode.next(mode));

  public readonly customPercentageErrorMessages = {
    required: $localize`Enter an amount above 0.00%, up to the max unlock`,
    min: $localize`Enter an amount above 0.00%, up to the max unlock`,
    max: $localize`The amount can not exceed 100.00%`
  };

  public readonly customAmountErrorMessages = {
    required: $localize`Enter an amount above $0.00, up to the Locked-In value`,
    min: $localize`Enter an amount above $0.00, up to the Locked-In value`,
    max: $localize`The amount can not exceed the Locked-In value`
  };

  public readonly toggleOptions: ButtonToggleOption[] = [
    { label: $localize`Percentage`, value: 'percent' },
    { label: $localize`Dollars`, value: 'dollar' }
  ];

  public setConfig(config: UnlockFundsModalConfig): void {
    const summary = summarizeUnlockFundsConfig(config);
    this._context.next({ config, summary });

    this.form.controls.amount.addValidators(Validators.max(summary.lockedBalance));
    this.form.controls.amount.updateValueAndValidity({ emitEvent: false });

    const saved = savedUnlockEntry(config);
    this._allocationMode.next(saved.mode);
    this._sourceMode.next(saved.mode);
    this.form.controls.percentage.setValue(saved.percentage, { emitEvent: false });
    this.form.controls.amount.setValue(saved.amount, { emitEvent: false });

    const isAtMax =
      saved.mode === 'percent' ? saved.percentage === MAX_UNLOCK_PERCENTAGE : saved.amount === summary.maxDollarAmount;
    if (summary.isMaxJurisdiction && isAtMax) {
      this.form.controls.useMax.setValue(true, { emitEvent: false });
    }
  }

  public onAmountBlur(): void {
    this._amountBlurred.next();
  }

  public toggleMode(newMode: AllocationMode): void {
    combineLatest([this._allocationMode, this._sourceMode, this.summary$])
      .pipe(take(1))
      .subscribe(([currentMode, sourceMode, { lockedBalance }]) => {
        if (currentMode === newMode) return;

        // Only ever convert away from the control the user entered a value into. Converting from the
        // already-converted control would re-apply rounding to a rounded value and inflate it on every toggle.
        if (newMode === 'dollar' && sourceMode === 'percent') {
          const percentage = this.form.controls.percentage.value;
          const amount = percentage === null ? null : floorToCents(lockedBalance * (percentage / 100));
          this.form.controls.amount.setValue(amount, { emitEvent: false });
        } else if (newMode === 'percent' && sourceMode === 'dollar') {
          const amount = this.form.controls.amount.value;
          if (amount === null) {
            this.form.controls.percentage.setValue(null, { emitEvent: false });
          } else if (lockedBalance > 0) {
            this.form.controls.percentage.setValue(ceilToHundredths((amount / lockedBalance) * 100), {
              emitEvent: false
            });
          }
        }

        this._allocationMode.next(newMode);
      });
  }

  public cancel(): void {
    this.ngbActiveModal.close(false);
  }

  public submit(): void {
    this.formSubmitted = true;

    combineLatest([this._context, this._allocationMode])
      .pipe(take(1))
      .subscribe(([{ config }, mode]) => {
        if (this.form.controls.revertToOriginal.value) {
          clearUnlock(config.rows);
          this.ngbActiveModal.close(true);
          return;
        }

        const activeControl = mode === 'percent' ? this.form.controls.percentage : this.form.controls.amount;
        if (activeControl.invalid) return;

        const percentage = this.form.controls.percentage.value;
        if (config.mode === 'pooled') {
          if (percentage === null) return;
          applyPooledUnlock(config.rows, percentage);
        } else {
          applySingleUnlock(config.rows, { mode, percentage, amount: this.form.controls.amount.value });
        }

        this.ngbActiveModal.close(true);
      });
  }

  private buildWarningAlert(mode: AllocationMode, maxDollarAmount: number): Alert {
    const maxUnlock = formatCurrency(
      maxDollarAmount,
      this.locale,
      getCurrencySymbol(this.currencyCode, 'wide', this.locale),
      this.currencyCode
    );
    const alert = new Alert();
    alert.title =
      mode === 'percent'
        ? $localize`The amount is above the max unlock of 50%`
        : $localize`The amount is above the max unlock of ${maxUnlock}:maxUnlock:`;
    alert.dismissable = false;
    alert.skipTitleTruncation = true;
    alert.colourId = AlertBackgroundColorEnum.BannerYellow;
    alert.colour.backgroundColour = '#fcf6eb';
    alert.colour.borderColour = '#d2811c';
    return alert;
  }
}
